import { loadBrowserErrors } from './loadBrowserErrors';

const maxBufferedErrors = 10;

type MonitoringOptions = {
  dsn: string | undefined;
  environment: string | undefined;
  hostname: string;
  load: () => Promise<{ initializeBrowserErrors: (dsn: string, errors: Error[]) => void }>;
  // When to download the SDK. Defaults to after the page has loaded and the browser is idle.
  whenIdle?: (run: () => void) => void;
};

// The SDK and its session-replay recorder are large. Fetching them while the page is still
// loading competes with the page's own text, fonts, and code, so they wait until it is done.
export function afterPageLoad(run: () => void) {
  const idle = () => {
    if (typeof window.requestIdleCallback === 'function')
      window.requestIdleCallback(run, { timeout: 3000 });
    else window.setTimeout(run, 1);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

// Keep early failures while the optional SDK loads, without blocking page rendering.
export function startBrowserErrorMonitoring(
  options: MonitoringOptions = {
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    hostname: window.location.hostname,
    load: loadBrowserErrors,
  }
) {
  const { dsn, environment, hostname, load, whenIdle = afterPageLoad } = options;
  if (
    environment !== 'production' ||
    !dsn ||
    !['scaledtech.consulting', 'www.scaledtech.consulting'].includes(hostname)
  ) {
    return;
  }

  const errors: Error[] = [];
  const remember = (value: unknown) => {
    if (value instanceof Error && errors.length < maxBufferedErrors) errors.push(value);
  };
  const onError = (event: ErrorEvent) => remember(event.error);
  const onRejection = (event: PromiseRejectionEvent) => remember(event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  const detach = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };

  whenIdle(() => {
    void load()
      .then(({ initializeBrowserErrors }) => {
        detach();
        initializeBrowserErrors(dsn, errors);
      })
      .catch(() => {
        detach();
      });
  });
}
